import {
  Card,
  Grid,
  Typography,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TablePagination,
  TableRow,
  Box
} from '@mui/material';
import React, { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useForm } from 'react-hook-form';
import { WorkFromHomeFilter, dropdownProps } from 'src/models/leave';
import { compareObjects } from 'src/utils/validate';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import NoRecord from '../resourcePool/NoRecord';
import { withStyles } from '@mui/styles';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { TableColumnCell } from 'src/components/TableColumnCell';
import EmployeeMultiSelect from '../workModel/common/EmployeeMultiSelect';
import SingleDatePicker from '../workModel/common/SingleDatePicker';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { getAllWorkFromHomeRequests } from 'src/services/leaveService';
import moment from 'moment';
import { AppActions } from 'src/store/AppReducer';
import StatusMultiSelect from './common/StatusMultiSelect';
import RmPmMultiSelect from './common/MultiSelect';
import VisibilityIcon from '@mui/icons-material/Visibility';
  
interface WorkFromHomeProps {
  permissions: any;
}
  
const WorkFromHomeTable: React.FC<WorkFromHomeProps> = ({ permissions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer.user);
  const pagination_wrapper_ref = useRef(null);

  const [data, setData] = useState<any>([]);
  
  const [toggleFilter, setToggleFilter] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [pmRmList, setPmRmList] = useState<dropdownProps[]>([]);

  const [view, setView] = useState(
    location.pathname === '/work-from-home' ? 'MY_WFH' :
    location.pathname === '/work-from-home/requests' && 'WFH_REQUESTS');

  const isHR = appUser?.Roles.split(',').includes('Super User') ||
  appUser?.Roles.split(',').includes('HR Manager') ||
  appUser?.Roles.split(',').includes('Assistant HR Manager') ||
  appUser?.Roles.split(',').includes('HR Executive');

  const isReportingManager = useSelector(
    (state: RootStateType) => state.appReducer.user.IsReportingManager
  );
  const isProjectManager = useSelector(
    (state: RootStateType) => state.appReducer.user.IsProjectManager
  );
  const userId = useSelector(
    (state: RootStateType) => state.appReducer.user.EmployeeId
  );

  let initialFilterValues = {
    requestId: null,
    pmRmIds: null,
    employees: null,
    startDate: null,
    endDate: null,
    status: null,
    limit: 10,
    offset: 0,
    isSubmitted: true
  } as WorkFromHomeFilter;
  const [filter, setFilter] = useState(initialFilterValues as WorkFromHomeFilter);
  const [prevFilter, setPrevFilter] = useState(initialFilterValues as WorkFromHomeFilter);

  const { register, handleSubmit, reset, setValue, watch } =
  useForm<WorkFromHomeFilter>({
    defaultValues: initialFilterValues
  });

  const startDate = watch('startDate');

  const onSubmit = () => {
    const filterCopy = {
      ...filter,
      isSubmitted: true,
      offset: 0,
      limit: 10
    }

    if(isFilterChanged){
      setFilter(filterCopy);
      setPrevFilter(filterCopy);
      setIsFilterChanged(false);
    } 
  };

  //clear the filter
  const resetFilterResult = () => {
    reset();

    const result = compareObjects(initialFilterValues, filter, "isSubmitted");
    !result && setFilter({
      ...initialFilterValues,
      startDate: null,
      endDate: null,
    });

    setPrevFilter({
      ...initialFilterValues,
      startDate: null,
      endDate: null,
    });
  };

  const isEndDateValid = (endDate, startDate) => {
    return new Date(endDate) >= new Date(startDate) || (endDate == null && startDate == null);
  };


  //reset the filter values when when click MY_WFH_REQUEST and WFH_REQUEST buttons
  useEffect(() => {
    reset();
    setFilter(initialFilterValues);
    setPrevFilter(initialFilterValues);
  }, [view]);


  //call API for load first time and when click filter button
  useEffect(() => {
    const payload = {
      requestId: null,
      pmRmIds: isHR ? filter.pmRmIds : 
        view !== 'WFH_REQUESTS' ? filter.pmRmIds : [userId],
      employeeIds: view === 'WFH_REQUESTS' ? filter.employees : [userId],
      status: filter.status,
      fromDate: filter.startDate ? moment(filter.startDate).format('YYYY-MM-DD') : null,
      toDate: filter.endDate ? moment(filter.endDate).format('YYYY-MM-DD') : null,
      isMyRequest: view === 'WFH_REQUESTS' ? false : true
    };
  
    //use this comment area inside the if/else block
     if(filter.isSubmitted){
      dispatch(AppActions.showLoader());
      const fetchAllWorkFromHomeRequests = (payload) => {
        getAllWorkFromHomeRequests(payload, 
          filter.offset * filter.limit,
          filter.limit
        ).then((res: any) => {
          if (res.data?.statusCode === 200) {
            const data = res?.data?.data
            setData(data?.wfhRequestSummery);
            setTotal(data?.totalCount);
  
            //set pm / rm values to dropdown
            if (data.wfhRequestSummery?.length > 0) {
              const rmPmList = data.wfhRequestSummery.map((item) => ({
                employeeId: item.pmRmId,
                name: item.pmRmName
              }));
              setPmRmList(rmPmList.reduce((acc, item) => {
                if(!acc.includes(e => e.employeeId === item)){
                  acc.push({
                    name: item.name,
                    value: item.employeeId
                  })
                }
                return acc;
              },[]));
            }
          }
          dispatch(AppActions.hideLoader());
        });
      };
  
      fetchAllWorkFromHomeRequests(payload);
      setFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }))
    } 
  }, [filter]);


  //set clear and filter button disable mechanisam
  useEffect(() => {
    const filterCopy = {
      startDate: filter.startDate,
      endDate: filter.endDate,
      employees: filter.employees,
      status: filter.status
    };

    const isNullish = Object.values(filterCopy).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(filterCopy).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      if(filter.startDate && filter.endDate == null){
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
      setIsClearDisabled(false);
    }
  }, [filter]);


  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);


  //handele pagination page change
  const handlePageChange = (event: any, newPage: number): void => {
    const validate = isEndDateValid(prevFilter.endDate, prevFilter.startDate);
    setFilter({
      ...prevFilter,
      offset: newPage,
      limit: filter.limit,
      isSubmitted: true,
      startDate: validate ? prevFilter.startDate : null
    });
  };

  //handele pagination limit change
  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const validate = isEndDateValid(prevFilter.endDate, prevFilter.startDate);
    setFilter({
      ...prevFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true,
      startDate: validate ? prevFilter.startDate : null
    });
  };


  // custom table cloumn cell
  const CustomTableBodyCloumnCell = ({content}) => {
    return (
      <TableColumnCell>
        <Typography
          paddingBottom="0.25rem"
          paddingTop="0.5rem"
          style={{ fontSize: '11px' }}
          variant="body2"
          gutterBottom
          noWrap
        >
          {content}
        </Typography>
      </TableColumnCell>
    )
  }

  const stringToBoolean = (str) => {
    if (typeof str !== 'string') {
        return false;
    }

    return str.toLowerCase().trim() === 'true';
  }

  return (
    <Fragment>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Work From Home
            <span style={{ fontSize: '12px', fontWeight: '550', color: '#223354'}}>
              {view === 'WFH_REQUESTS' ? ' / WFH history' : ' / My WFH history'}
            </span>
          </Typography>
        </Grid>
        <Grid item justifyContent="flex-end">
          {/* show only if screen is WFH_REQUESTS */}
          {view === 'WFH_REQUESTS' &&
            <Button
              sx={{
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              style={{ height: '1.75rem' }}
              onClick={() => {
                setView('MY-WFH');
                navigate(`/work-from-home`);
                setToggleFilter(false);
              }}
            >
              My WFH history
            </Button>
          }
          {/* show only if logged user is HR or RM/PM */}
          {(location.pathname === '/work-from-home' && (isHR || stringToBoolean(isReportingManager) === true || stringToBoolean(isProjectManager) === true )) &&
            <Button
              sx={{
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              style={{ height: '1.75rem' }}
              onClick={() => {
                setView('WFH_REQUESTS')
                navigate(`/work-from-home/requests`);
                setToggleFilter(false);
              }}
            >
              WFH history
            </Button>
          }
          {permissions.some(perm =>
              perm.name === 'Apply Work From Home' && perm.value === true
            ) && 
            <Button
              sx={{
                ml: 1,
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              startIcon={<AddTwoToneIcon fontSize="small" />}
              variant="contained"
              style={{ height: '1.75rem' }}
              onClick={() => {
                navigate(`/work-from-home/apply`);
              }}
            >
              Apply Work From Home
            </Button>
          }
        </Grid>
      </Grid>
      <div className='resource-pool'>
        <Card>
          <Grid sx={{ px: 2, py: 2 }} item xs={6}>
            <Button
              id="btn-setToggleFilter"
              sx={{
                mt: { xs: 2, md: 0 },
                color: `${!toggleFilter && 'gray'}`
              }}
              variant="outlined"
              startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
              onClick={() => {
                setToggleFilter(!toggleFilter);
              }}
              style={{ height: '1.75rem', fontSize: '12px' }}
            >
              Filters
            </Button>
          </Grid>
          {toggleFilter && (
            <Grid px={2} pb={2} style={{margin: 0}}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center'
                  }}
                >
                  {location.pathname !== '/work-from-home' &&
                    <Grid item xs={3} width='20%'>
                      <EmployeeMultiSelect
                        {...register('employees')}
                        value={filter?.employees || []}
                        onChange={(e: any) => {
                          setFilter((prev) => ({
                            ...prev,
                            employees: e.target.value
                          }));
                          setValue('employees', e.target.value);
                          setIsFilterChanged(true);
                        }}/>
                    </Grid>
                  }
                  {/* {(isHR && (stringToBoolean(isReportingManager) === false && stringToBoolean(isProjectManager) === false)) &&
                  <Grid item xs={3} width='20%'>
                    <RmPmMultiSelect
                      {...register('pmRmIds')}
                      title='PM / RM'
                      dropdownList={pmRmList || []}
                      value={filter?.pmRmIds || []}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          pmRmIds: e.target.value
                        }));
                        setValue('pmRmIds', e.target.value);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid>
                  } */}
                  {/* <Grid item xs={3} width='20%'>
                    <StatusMultiSelect
                      {...register('status')}
                      value={filter?.status || []}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          status: e.target.value
                        }));
                        setValue('status', e.target.value);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid> */}
                  <Grid item xs={3} width='20%'>
                    <SingleDatePicker
                      {...register('startDate')}
                      fullWidth={true}
                      label='Start Date'
                      value={filter?.startDate || null}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          startDate: e,
                          endDate: e > prev.endDate ? null : prev.endDate
                        }));
                        setValue('startDate', e);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid>
                  <Grid item xs={3} width='20%'>
                    <SingleDatePicker
                      fullWidth={true}
                      {...register('endDate', {
                        validate: value => isEndDateValid(value, startDate) 
                      })}
                      disabled={filter?.startDate === null}
                      minDate={filter?.startDate}
                      label='End Date'
                      value={filter?.endDate || null}
                      onChange={(e: any) => {
                        setFilter((prev) => ({
                          ...prev,
                          endDate: e
                        }));
                        setValue('endDate', e);
                        setIsFilterChanged(true);
                      }}/>
                      <div style={{fontSize: '9px', color: 'red', position: 'absolute', marginLeft: '15px'}}>
                        {filter?.startDate && filter?.endDate == null &&
                        "End date is mandatory field"}
                      </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    marginTop: '5px',
                  }}
                >
                  <Grid
                    item xs={3}
                    width='60%'
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                      marginTop: '8px',
                      display: 'flex'
                    }}
                  >
                    <Button
                      sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                      variant="outlined"
                      onClick={resetFilterResult}
                      disabled={isClearDisabled}
                      className={isClearDisabled &&'clear-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                      id="btn-resetFilterResult"
                    >
                      Clear
                    </Button>
                    <Button
                      sx={{ mt: { xs: 2, md: 0 } }}
                      id="btn-onSubmit"
                      variant="outlined"
                      type="submit"
                      disabled={isDisabled}
                      className={isDisabled &&'filter-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                    >
                      Filter
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}
          <Divider />
          <TableContainer className="tableContainer" style={{ height: (data?.length === 0 || !data) ? '250px' : 'auto' }}>
            <Table style={{ borderCollapse: 'separate', position: 'relative' }} size="small">
              <TableHead>
                <CustomTableRow style={{position: 'sticky', top: 0, zIndex: 1}}>
                  <TableHeaderCell content="Employee Name" smallFont/>
                  <TableHeaderCell content="From Date" smallFont/>
                  <TableHeaderCell content="To Date" smallFont/>
                  <TableHeaderCell content="Day Count" smallFont/>
                  <TableHeaderCell content="PM / RM" smallFont/>
                  {/* <TableHeaderCell content="Status" smallFont/> */}
                  {/* <TableHeaderCell content="Action" smallFont sticky/> */}
                </CustomTableRow>
              </TableHead>
              <TableBody>
                {data &&
                  data?.length > 0 &&
                  data.map((emp, i) => {
                    return (
                      <TableRow
                        style={{borderBottom: '1px solid #eee'}}
                        hover
                        key={emp.id}
                      >
                        <CustomTableBodyCloumnCell content={emp?.employeeName}/>
                        <CustomTableBodyCloumnCell content={emp?.fromDate}/>
                        <CustomTableBodyCloumnCell content={emp?.toDate}/>
                        <CustomTableBodyCloumnCell content={emp?.daysCount}/>
                        <CustomTableBodyCloumnCell content={emp?.pmRmName}/>
                        {/* <CustomTableBodyCloumnCell content={emp?.status}/> */}
                        {/* <TableColumnCell sticky>
                          <div
                            style={{ background: 'white', marginLeft: '0.9rem' }}
                          >
                            <VisibilityIcon
                              onClick={() => {
                                navigate(`/work-from-home/apply/${emp?.id}`)
                              }}
                              style={{
                                fontSize: '17px',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        </TableColumnCell> */}
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
            {(data?.length === 0 || !data) && (
              <NoRecord/>
            )}
          </TableContainer>  
          <Box p={1} ref={pagination_wrapper_ref} sx={{borderTop: '1px solid #eee'}}>
            <TablePagination
              sx={{
                fontSize: '12px',
                '.MuiTablePagination-displayedRows': {
                  fontSize: '12px'
                },
                '.MuiTablePagination-selectLabel': {
                  fontSize: '12px'
                }
              }}
              component="div"
              count={total ? total : 0}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={filter.offset}
              rowsPerPage={filter.limit}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
            />
          </Box>
        </Card>
      </div>
    </Fragment>
  );
};

export default WorkFromHomeTable;
  